import { Button, DialogRoot, DialogTrigger, Item } from '@ibsys/kit';
import { XCircleIcon } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';
import { AddNestedItemsDialog } from './AddNestedItemsDialog';
import { NestedItemCard } from './NestedItemCard';

type Labels = {
  title: string;
  addButton: string;
  emptyState: string;
  dialogTitle: string;
  searchPlaceholder?: string;
  noItemsFound?: string;
  cancel?: string;
  add?: string;
};

type NestedItemsProps<T> = {
  labels: Labels;
  value: T[];
  onChange: (items: T[]) => void;
  allItems: T[];
  renderTitle: (item: T) => ReactNode;
  renderDescription?: (item: T) => ReactNode;
  getItemId: (item: T) => string;
  disabled?: boolean;
  className?: string;
  searchFilter?: (item: T, searchQuery: string) => boolean;
};

export const NestedItems = <T,>({
  labels,
  value,
  onChange,
  allItems,
  renderTitle,
  renderDescription,
  getItemId,
  disabled = false,
  className,
  searchFilter,
}: NestedItemsProps<T>) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const currentItemIds = useMemo(() => value.map(getItemId), [value, getItemId]);

  const availableItems = useMemo(() => {
    return allItems.filter((item) => !currentItemIds.includes(getItemId(item)));
  }, [allItems, currentItemIds, getItemId]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleSaveDialog = (newItems: T[]) => {
    onChange([...value, ...newItems]);
    setIsDialogOpen(false);
  };

  const handleRemoveItem = (id: string) => {
    onChange(value.filter((item) => getItemId(item) !== id));
  };

  const getItemById = (id: string): T | undefined => {
    return allItems.find((item) => getItemId(item) === id);
  };

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className={`flex flex-col gap-4 ${className || ''}`}>
        <div className="flex gap-5 items-center">
          <p className="font-medium">{labels.title}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenDialog}>
                {labels.addButton}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {value.map((item) => {
            const itemData = getItemById(getItemId(item));
            if (!itemData) {
              return (
                <Item key={getItemId(item)} className="flex items-center justify-between p-3 border rounded-md">
                  <span className="font-mono text-sm">{getItemId(item)}</span>
                  {!disabled && (
                    <Button
                      icon={<XCircleIcon />}
                      variant="ghost"
                      color="destructive"
                      onClick={() => handleRemoveItem(getItemId(item))}
                    />
                  )}
                </Item>
              );
            }
            return (
              <NestedItemCard
                key={getItemId(item)}
                item={itemData}
                renderTitle={renderTitle}
                renderDescription={renderDescription}
                action={
                  !disabled && (
                    <Button
                      icon={<XCircleIcon />}
                      variant="ghost"
                      color="destructive"
                      onClick={() => handleRemoveItem(getItemId(item))}
                    />
                  )
                }
              />
            );
          })}
          {value.length === 0 && <p className="text-sm text-muted-foreground">{labels.emptyState}</p>}
        </div>
      </div>

      <AddNestedItemsDialog
        onOpenChange={setIsDialogOpen}
        availableItems={availableItems}
        onSave={handleSaveDialog}
        labels={{
          ...labels,
        }}
        renderTitle={renderTitle}
        renderDescription={renderDescription}
        getItemId={getItemId}
        searchFilter={searchFilter}
      />
    </DialogRoot>
  );
};
